import { useEffect, useState } from "react"
import {
  Button,
  Typography,
  Modal,
  Dropdown,
  Menu,
  message,
} from "antd"
import { PlusOutlined, MoreOutlined } from "@ant-design/icons"
import PanelLayout from "../../Layout/PanelLayout"
import TableBox from "../../components/TableBox/TableBox"
import apiClient from "../../Utils/apiClient"
import CreateRole from "./CreateRole" // your form modal for role creation
import { useNavigate } from "react-router-dom"
import "../Operations/Operations.scss"

const { Text } = Typography

const Roles = () => {
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(false)
  const [openAddRole, setOpenAddRole] = useState(false)
  const navigate = useNavigate()

  const fetchRoles = async () => {
    try {
      setLoading(true)
      const res = await apiClient("/roles", "GET")
      if (res?.success) setRoles(res.data || [])
    } catch (err) {
      message.error("Failed to load roles")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRoles()
  }, [])

  const handleEdit = (record) => {
    navigate(`/roles/${record._id}`, {
      state: record,
    })
  }

  const handleDelete = async (record) => {
    Modal.confirm({
      title: `Delete Role: ${record.name}`,
      content: "Are you sure you want to delete this role?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await apiClient(`/roles/${record._id}`, "DELETE")
          message.success("Role deleted")
          fetchRoles()
        } catch (error) {
          message.error("Failed to delete role")
        }
      },
    })
  }

  const menu = (record) => (
    <Menu>
      <Menu.Item key="edit" onClick={() => handleEdit(record)}>
        Edit
      </Menu.Item>
      <Menu.Item key="delete" danger onClick={() => handleDelete(record)}>
        Delete
      </Menu.Item>
    </Menu>
  )

  const columns = [
    {
      title: "Assignable Role",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Text strong className="ops-name">
          {String(text || "").charAt(0).toUpperCase() +
            String(text || "").slice(1).toLowerCase()}
        </Text>
      ),
    },
    {
      title: "Manage Role",
      key: "actions",
      align: "center",
      width: 140,
      render: (_, record) => (
        <Dropdown overlay={menu(record)} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ]

  const kpis = [
    {
      key: "total",
      label: "Roles",
      value: roles.length,
      tone: "#2563eb",
      soft: "rgba(59, 130, 246, 0.14)",
    },
    {
      key: "admin",
      label: "Admin-style",
      value: roles.filter((r) =>
        /admin|network/i.test(String(r.name || ""))
      ).length,
      tone: "#7c3aed",
      soft: "rgba(167, 139, 250, 0.14)",
    },
  ]

  return (
    <PanelLayout title="Manage Roles"
      subtitle="Create roles and choose which console permissions each one unlocks.">
      <div className="ops-kpis">
        {kpis.map((card) => (
          <div
            key={card.key}
            className="ops-kpi ops-kpi--static"
            style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
          >
            <div>
              <span className="ops-kpi-label">{card.label}</span>
              <strong style={{ fontSize: 17 }}>{card.value}</strong>
            </div>
          </div>
        ))}
      </div>
      <TableBox
        className="dashboard-inventory-table"
        page="Roles"
        title={
          <>
            Roles <Text type="secondary">({roles.length})</Text>
          </>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setOpenAddRole(true)}
          >
            Add Role
          </Button>
        }
        rowKey="_id"
        columns={columns}
        data={roles}
        loading={loading}
      />

      <Modal
        title="Add Role"
        open={openAddRole}
        footer={null}
        onCancel={() => setOpenAddRole(false)}
        destroyOnClose
      >
        <CreateRole
          onCancel={() => setOpenAddRole(false)}
          onSuccess={() => {
            setOpenAddRole(false)
            fetchRoles()
          }}
        />
      </Modal>
    </PanelLayout>
  )
}

export default Roles
